import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';

import { EstimationTile, SummaryEstimation } from '@bingads-webui-campaign-react/recommendation-estimation-tile';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import {
  withAdsAutoApply,
  RECOMMENDATION_TYPES,
  recommendationInProductUpdateConfigs,
  additionDetailPitchingConfig,
} from '@bingads-webui-campaign/recommendation-core';
import {
  isInProductUpdateBannerAvailable,
  isAdditionalPitchingAvailable,
  InProdUpdateNotificationBanner,
  WarnNotificationBanner
} from '@bingads-webui-campaign/recommendation-notification';
import { CouponTagNew } from '@bingads-webui-react/feature-adoption-coupon';
import { RecommendationOptimizationScoreTag } from '@bingads-webui-campaign-react/recommendation-os-tag';
import { StyledRecommendationAutpApplyBanner } from '@bingads-webui-campaign-react/recommendation-auto-apply-banner';
import { MessageBar, MessageBarType } from '@bingads-webui-react/fabric-control';
import { AdditionalPitching } from './additional-pitching';

export class DetailsSummary extends React.PureComponent {
  static propTypes = {
    iconClasses: PropTypes.string.isRequired,
    iconClassesVNext: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    pitching: PropTypes.string.isRequired,
    helpId: PropTypes.string.isRequired,
    estimates: PropTypes.objectOf(PropTypes.any),
    coupon: PropTypes.objectOf(PropTypes.any),
    i18n: PropTypes.PropTypes.shape({
      getString: PropTypes.func.isRequired,
    }).isRequired,
    cid: PropTypes.number,
    uid: PropTypes.number,
    aid: PropTypes.number,
    odata: PropTypes.PropTypes.shape({
      get: PropTypes.func.isRequired,
    }),
    type: PropTypes.string,
    optInStatus: PropTypes.bool,
    applyDate: PropTypes.number,
    recommendationsCount: PropTypes.number,
    isEstimate: PropTypes.bool,
    needNRTDisclaimer: PropTypes.bool,
    appConfig: PropTypes.objectOf(PropTypes.any),
    isMCC: PropTypes.bool,
    isNrt: PropTypes.bool,
    hasAIAssetRecommendation: PropTypes.bool,
    accounts: PropTypes.arrayOf(PropTypes.shape({
      accountId: PropTypes.string,
      accountName: PropTypes.string,
      optInStatus: PropTypes.bool,
      adsByBingAutoApply: PropTypes.string,
    })),
    permissions: PropTypes.objectOf(PropTypes.any),
    optimizationScore: PropTypes.number,
    preferencesService: PropTypes.objectOf(PropTypes.any),
    overallOptimizationScoreBar: PropTypes.shape({
      Gained: PropTypes.number,
      Dismissed: PropTypes.number,
      Available: PropTypes.number,
    }),
    linkType: PropTypes.string,
    isOptimizationScoreOn: PropTypes.bool,
    newI18n: PropTypes.PropTypes.shape({
      getString: PropTypes.func,
    }),
    isDismissedCategory: PropTypes.bool,
    showAutoApplyBanner: PropTypes.bool,
    isAutoApplyRecommendationEnabled: PropTypes.bool,
    viewSettings: PropTypes.func,
    thirdPartyInfos: PropTypes.arrayOf(PropTypes.shape({
      Level: PropTypes.number,
      ThirdPartyName: PropTypes.string,
    })),
    isAutoApplyOptIn: PropTypes.bool,
  };

  static defaultProps = {
    estimates: null,
    cid: null,
    uid: null,
    aid: null,
    odata: null,
    type: null,
    optInStatus: false,
    applyDate: 0,
    recommendationsCount: 0,
    appConfig: null,
    isEstimate: true,
    needNRTDisclaimer: false,
    coupon: null,
    isMCC: false,
    isNrt: false,
    hasAIAssetRecommendation: false,
    accounts: [],
    permissions: {},
    optimizationScore: null,
    preferencesService: null,
    overallOptimizationScoreBar: null,
    linkType: 'helpPopup',
    isOptimizationScoreOn: false,
    newI18n: {
      getString: _.noop,
    },
    isDismissedCategory: false,
    showAutoApplyBanner: false,
    isAutoApplyRecommendationEnabled: false,
    viewSettings: _.noop,
    thirdPartyInfos: undefined,
    isAutoApplyOptIn: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      campaignAutoApplyStatus: {},
    };
  }

  render() {
    const {
      iconClasses,
      iconClassesVNext,
      description,
      estimates,
      pitching,
      helpId,
      i18n,
      title,
      cid,
      aid,
      uid,
      odata,
      type,
      optInStatus,
      applyDate,
      recommendationsCount,
      appConfig,
      isEstimate,
      needNRTDisclaimer,
      coupon,
      permissions,
      optimizationScore,
      isNrt,
      hasAIAssetRecommendation,
      preferencesService,
      overallOptimizationScoreBar,
      linkType,
      isOptimizationScoreOn,
      newI18n,
      isDismissedCategory,
      showAutoApplyBanner,
      isAutoApplyRecommendationEnabled,
      viewSettings,
      thirdPartyInfos,
      isMCC,
      isAutoApplyOptIn,
    } = this.props;

    const AutoApplyBanner = isAutoApplyRecommendationEnabled ?
      StyledRecommendationAutpApplyBanner : withAdsAutoApply(StyledRecommendationAutpApplyBanner);
    const withEstimateTileStyle = !isOptimizationScoreOn && estimates ? 'details-summary-with-estimate' : '';
    const withOptimizationScore = isOptimizationScoreOn ? 'with-optimization-score' : '';
    const summary = { isNrt, hasAIAssetRecommendation };
    const inProductUpdateConfig = recommendationInProductUpdateConfigs[type];
    const isDefinedAdditionalPitching = isAdditionalPitchingAvailable(type, permissions, summary);
    const isRSARecAutoApplyNotOptIn = type === RECOMMENDATION_TYPES.RESPONSIVE_SEARCH_ADS ?
      this.state.campaignAutoApplyStatus[RECOMMENDATION_TYPES.RESPONSIVE_SEARCH_ADS] !== 'true' :
      true;

    const tagManager = thirdPartyInfos && thirdPartyInfos.length > 0 ?
      thirdPartyInfos[0].ThirdPartyName : null;
    const additionalArgs = _.isNull(tagManager) ? {} : { tagManager };

    return (
      <div className={`recommendation-details-summary ${withEstimateTileStyle} ${withOptimizationScore}`}>
        <div className="details-summary-left">
          <div className="details-summary-icon">
            <div className={`details-header-icon ${iconClasses}`}>
              <span className={`iconba iconba-${iconClassesVNext}`} />
            </div>
            <div className="details-summary-title">{title}</div>
            {coupon && (
              <CouponTagNew
                coupon={coupon}
                i18n={i18n}
              />
            )}
            {isOptimizationScoreOn && !_.isEmpty(overallOptimizationScoreBar) &&
              !isDismissedCategory &&
              <RecommendationOptimizationScoreTag
                className="details-summary-os-tag"
                i18n={i18n}
                optimizationScore={optimizationScore}
                newI18n={newI18n}
              />
            }
          </div>
          {inProductUpdateConfig && inProductUpdateConfig.warnNotification &&
            <WarnNotificationBanner
              type={type}
              preferencesService={preferencesService}
              newI18n={newI18n}
              uid={uid}
              cid={cid}
              aid={aid}
              isMCC={isMCC}
              isAutoApplyOptIn={isAutoApplyOptIn}
            />
          }
          {isInProductUpdateBannerAvailable(type, permissions, summary) &&
            <InProdUpdateNotificationBanner
              type={type}
              preferencesService={preferencesService}
              i18n={i18n}
              additionalArgs={additionalArgs}
            />
          }
          {type === RECOMMENDATION_TYPES.CREATE_CONVERSION_GOAL &&
            <MessageBar
              className="message-bar"
              isMultiline={false}
              messageBarType={MessageBarType.info}
            >
              {newI18n.getString(_TL_('In the default setting, conversions from recommended goals will appear in the “Conversions” column and will affect any enabled automated bidding strategies.'))}
            </MessageBar>
          }
          <div
            className="details-summary-description"
          >
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </div>
          <div className="details-summary-pitching">
            <LearnMoreLink
              text={pitching}
              topic={helpId}
              linkText={i18n.getString('Learn_more_link_text')}
              linkType={linkType}
              noLink={type === RECOMMENDATION_TYPES.SSC_2_PMAX_MIGRATION}
            />
            {type === RECOMMENDATION_TYPES.CREATE_CONVERSION_GOAL &&
              <div>
                <LearnMoreLink
                  text={newI18n.getString(_TL_('New! Clarity is Microsoft\'s free behavioral analysis tool that helps you understand how customers interact with your website. Create a conversion goal with click events on your website based on Clarity. In addition to tracking clicks on your specified webpage, we\'ll also track other pages on your website that use buttons with similar text and placement.'))}
                  topic="ext60153"
                  linkText={i18n.getString('Learn_more_link_text')}
                  linkType={linkType}
                />
              </div>
            }
            <AdditionalPitching
              additionDetailPitchingConfig={additionDetailPitchingConfig}
              aid={aid}
              cid={cid}
              i18n={i18n}
              inProductUpdateConfig={inProductUpdateConfig}
              isDefinedAdditionalPitching={isDefinedAdditionalPitching}
              isRSARecAutoApplyNotOptIn={isRSARecAutoApplyNotOptIn}
              needNRTDisclaimer={needNRTDisclaimer}
              newI18n={newI18n}
              permissions={permissions}
              type={type}
              uid={uid}
            />
          </div>
          {isOptimizationScoreOn && estimates &&
            <div className="summary-estimation">
              <SummaryEstimation {...estimates} />
            </div>
          }
          <div className={`details-past-performance-blurb ${withOptimizationScore}`}>
            {isEstimate && estimates && i18n.getString('Details_View_Past_Performance_Blurb')}
            {type === RECOMMENDATION_TYPES.IMPROVE_MULTIMEDIA_ADS &&
              <div>
                {isEstimate && estimates && newI18n.getString(_TL_('We recommend using these ad texts, headlines, and images generated from your websites.'))}
              </div>
            }
          </div>
        </div>
        {!isOptimizationScoreOn && estimates &&
          <React.Fragment>
            <div className="separator-col" />
            <div className="details-summary-estimates">
              <EstimationTile {...estimates} />
            </div>
          </React.Fragment>
        }
        {showAutoApplyBanner &&
          <AutoApplyBanner
            i18n={i18n}
            cid={cid}
            aid={aid}
            newI18n={newI18n}
            applyDate={applyDate}
            recommendationsCount={recommendationsCount}
            appConfig={appConfig}
            odata={odata}
            callback={(campaignAutoApplyStatus) => {
              if (campaignAutoApplyStatus[type] !== this.state.campaignAutoApplyStatus[type]) {
                this.setState({ campaignAutoApplyStatus });
              }
            }}
            permissions={permissions}
            type={type}
            campaignAutoApplyStatus={isAutoApplyRecommendationEnabled ? { [type]: 'true' } : {}}
            optInStatus={optInStatus}
            isAutoApplyRecommendationEnabled={isAutoApplyRecommendationEnabled}
            viewSettings={viewSettings}
          />}
      </div>
    );
  }
}

